import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Archive, ArchiveRestore, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { BILLING_MODELS, ENGAGEMENT_STATUSES, RETAINER_PERIODS, archiveEngagement, createRate, deleteRate, restoreEngagement, updateEngagement } from '@mkcore/engagements/schema';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  NativeSelect,
  PageHeader,
  Skeleton,
  TBody,
  THead,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
} from '@mkcore/ui';
import { ApiError, newIdempotencyKey } from '@/lib/api';
import { formatMoney } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { engagementQuery, ratesQuery } from '@/features/engagements/queries';
import { centsToInput, hoursToMinutes, inputToCents, minutesToHours } from '@/features/engagements/money';
import { EngagementStatusBadge, ModelLabel } from '@/features/engagements/labels';
import { ConfirmButton } from '@/components/confirm';
import { AttachmentsPanel } from '@/features/files/attachments-panel';
import { Activities } from '@/features/crm/leads';
import { RecordTasks } from '@/features/projects/tasks';
import { ChangeRequests } from '@/features/projects/change-requests';
import { EngagementSupportCard } from '@/features/support/sla';
import { TermsPanel } from '@/features/billing/terms-panel';
import { RecurringPanel, RetainerUsagePanel } from '@/features/billing/recurring-panel';
import { EngagementPortal } from '@/features/portal/engagement-portal';
import { CloseoutCard } from '@/features/engagements/closeout';
import { toFormErrors } from '@/features/projects/form-errors';

export const Route = createFileRoute('/_app/engagements/$id')({ component: EngagementDetail });

type Form = {
  name: string;
  billingModel: (typeof BILLING_MODELS)[number];
  status: (typeof ENGAGEMENT_STATUSES)[number];
  startsOn: string;
  endsOn: string;
  currency: string;
  fixedAmount: string;
  retainerPeriod: (typeof RETAINER_PERIODS)[number];
  retainerHours: string;
  overageRate: string;
  retainerRollover: boolean;
  purchaseOrder: string;
  customerReference: string;
  budgetHours: string;
  budgetAmount: string;
  targetMargin: string;
  billableDefault: boolean;
  agentTimeBillable: boolean;
  notes: string;
};

/** One engagement: its billing model, budget and rates (CO-0168, CO-0169, CO-0271). */
function EngagementDetail() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const message = useErrorMessage();
  const eng = useQuery(engagementQuery(id));
  const canUpdate = useCan('engagements:update');
  const canBilling = useCan('billing:read');
  const canCrm = useCan('crm:read');
  const canProjects = useCan('projects:read');
  const canSupport = useCan('support:read');
  const [form, setForm] = useState<Form | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => setForm(null), [id, eng.data?.version]);
  // The retainer usage shows allowance and rollover of the engagement, so a save refreshes it too (CO-0273).
  const invalidate = [['engagement'], ['billing', 'invoice', 'retainer']];
  const save = useActionMutation(updateEngagement, { invalidate, success: t('common.saved'), silentErrors: true });
  const archive = useActionMutation(archiveEngagement, { invalidate, success: t('engagements.archivedDone') });
  const restore = useActionMutation(restoreEngagement, { invalidate, success: t('engagements.restoredDone') });

  if (eng.isError) return <ErrorState title={t('errors.generic')} description={message(eng.error)} />;
  if (!eng.data) return <Skeleton className="h-96" />;
  const e = eng.data;
  const locked = !canUpdate || !!e.archivedAt;
  const f: Form = form ?? {
    name: e.name,
    billingModel: e.billingModel,
    status: e.status,
    startsOn: e.startsOn ?? '',
    endsOn: e.endsOn ?? '',
    currency: e.currency,
    fixedAmount: centsToInput(e.fixedAmountCents),
    retainerPeriod: e.retainerPeriod ?? 'month',
    retainerHours: minutesToHours(e.retainerMinutes),
    overageRate: centsToInput(e.overageSellCents),
    retainerRollover: e.retainerRollover,
    purchaseOrder: e.purchaseOrder ?? '',
    customerReference: e.customerReference ?? '',
    budgetHours: minutesToHours(e.budgetMinutes),
    budgetAmount: centsToInput(e.budgetCents),
    targetMargin: e.targetMarginBp === null ? '' : String(e.targetMarginBp / 100).replace('.', ','),
    billableDefault: e.billableDefault,
    agentTimeBillable: e.agentTimeBillable,
    notes: e.notes,
  };
  const set = (patch: Partial<Form>) => setForm({ ...f, ...patch });
  const text = (k: keyof Form, label: string, extra: { type?: string; hint?: string; placeholder?: string } = {}) => (
    <Field label={label} hint={extra.hint} error={errors[k]}>
      {(p) => <Input {...p} type={extra.type} placeholder={extra.placeholder} value={f[k] as string} disabled={locked} onChange={(ev) => set({ [k]: ev.target.value } as Partial<Form>)} />}
    </Field>
  );
  const submit = () =>
    save.mutate(
      {
        input: {
          id,
          name: f.name,
          billingModel: f.billingModel,
          status: f.status,
          startsOn: f.startsOn || null,
          endsOn: f.endsOn || null,
          currency: f.currency,
          fixedAmountCents: f.billingModel === 'fixed' ? inputToCents(f.fixedAmount) : null,
          retainerPeriod: f.billingModel === 'retainer' ? f.retainerPeriod : null,
          retainerMinutes: f.billingModel === 'retainer' ? hoursToMinutes(f.retainerHours) : null,
          overageSellCents: f.billingModel === 'retainer' ? inputToCents(f.overageRate) : null,
          retainerRollover: f.billingModel === 'retainer' && f.retainerRollover,
          purchaseOrder: f.purchaseOrder || null,
          customerReference: f.customerReference || null,
          budgetMinutes: hoursToMinutes(f.budgetHours),
          budgetCents: inputToCents(f.budgetAmount),
          targetMarginBp: f.targetMargin.trim() === '' ? null : Math.round(Number(f.targetMargin.replace(',', '.')) * 100),
          billableDefault: f.billableDefault,
          agentTimeBillable: f.agentTimeBillable,
          notes: f.notes,
        },
        ifMatch: e.version,
      },
      { onError: (err) => setErrors(err instanceof ApiError ? { ...toFormErrors(err.fieldErrors()), _: message(err) } : { _: message(err) }), onSuccess: () => setErrors({}) },
    );

  return (
    <>
      <Link to="/engagements" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('engagements.title')}
      </Link>
      <PageHeader
        eyebrow={
          <Link to="/org/$id" params={{ id: e.organisationId }} className="hover:underline">
            {e.organisationName}
          </Link>
        }
        title={e.name}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <EngagementStatusBadge status={e.status} />
            <ModelLabel model={e.billingModel} />
            {e.archivedAt && <Badge tone="neutral">{t('org.archived')}</Badge>}
          </span>
        }
        actions={
          canUpdate &&
          (e.archivedAt ? (
            <Button variant="secondary" loading={restore.isPending} onClick={() => restore.mutate({ input: { id } })}>
              <ArchiveRestore /> {t('org.restore')}
            </Button>
          ) : (
            <ConfirmButton icon={<Archive />} label={t('org.archive')} title={t('engagements.archiveTitle')} description={t('engagements.archiveText')} onConfirm={() => archive.mutateAsync({ input: { id } })} />
          ))
        }
      />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card className="grid content-start gap-3 p-4">
          {text('name', t('common.name'))}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('engagements.fields.billingModel')} hint={t(`engagements.modelHint.${f.billingModel}`)} error={errors.billingModel}>
              {(p) => (
                <NativeSelect {...p} value={f.billingModel} disabled={locked} onChange={(ev) => set({ billingModel: ev.target.value as Form['billingModel'] })}>
                  {BILLING_MODELS.map((v) => (
                    <option key={v} value={v}>
                      {t(`engagements.model.${v}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field label={t('common.status')}>
              {(p) => (
                <NativeSelect {...p} value={f.status} disabled={locked} onChange={(ev) => set({ status: ev.target.value as Form['status'] })}>
                  {ENGAGEMENT_STATUSES.map((v) => (
                    <option key={v} value={v}>
                      {t(`engagements.status.${v}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          </div>
          {f.billingModel === 'fixed' && text('fixedAmount', t('engagements.fields.fixedAmount'), { placeholder: '12500.00' })}
          {f.billingModel === 'retainer' && (
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label={t('engagements.fields.retainerPeriod')}>
                {(p) => (
                  <NativeSelect {...p} value={f.retainerPeriod} disabled={locked} onChange={(ev) => set({ retainerPeriod: ev.target.value as Form['retainerPeriod'] })}>
                    {RETAINER_PERIODS.map((v) => (
                      <option key={v} value={v}>
                        {t(`engagements.period.${v}`)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              {text('retainerHours', t('engagements.fields.retainerHours'))}
              {text('overageRate', t('engagements.fields.overageRate'), { hint: t('engagements.fields.overageHint') })}
              <label className="flex items-center gap-2 text-sm sm:col-span-3">
                <Checkbox checked={f.retainerRollover} disabled={locked} onCheckedChange={(v) => set({ retainerRollover: v === true })} /> {t('engagements.fields.retainerRollover')}
              </label>
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-3">
            {text('startsOn', t('engagements.fields.startsOn'), { type: 'date' })}
            {text('endsOn', t('engagements.fields.endsOn'), { type: 'date' })}
            {text('currency', t('org.fields.currency'))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {text('purchaseOrder', t('engagements.fields.purchaseOrder'), { hint: t('engagements.fields.purchaseOrderHint') })}
            {text('customerReference', t('engagements.fields.customerReference'))}
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {text('budgetHours', t('engagements.fields.budgetHours'))}
            {text('budgetAmount', t('engagements.fields.budgetAmount'))}
            {text('targetMargin', t('engagements.fields.targetMargin'), { hint: t('engagements.fields.targetMarginHint') })}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={f.billableDefault} disabled={locked} onCheckedChange={(v) => set({ billableDefault: v === true })} /> {t('engagements.fields.billableDefault')}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={f.agentTimeBillable} disabled={locked} onCheckedChange={(v) => set({ agentTimeBillable: v === true })} /> {t('engagements.fields.agentTimeBillable')}
          </label>
          <Field label={t('org.fields.notes')}>{(p) => <Textarea {...p} rows={3} value={f.notes} disabled={locked} onChange={(ev) => set({ notes: ev.target.value })} />}</Field>
          {errors._ && (
            <p role="alert" className="text-sm text-danger">
              {errors._}
            </p>
          )}
          {!locked && (
            <div className="flex justify-end">
              <Button disabled={!form} loading={save.isPending} onClick={submit}>
                {t('common.save')}
              </Button>
            </div>
          )}
        </Card>
        <div className="grid content-start gap-6">
          <Rates engagementId={id} currency={e.currency} locked={locked} />
          {canBilling && e.billingModel === 'retainer' && e.retainerPeriod && <RetainerUsagePanel engagementId={id} />}
          {canBilling && <TermsPanel engagementId={id} currency={e.currency} fixed={e.billingModel === 'fixed'} />}
          {canBilling && <RecurringPanel engagementId={id} currency={e.currency} />}
          <CloseoutCard engagementId={id} closed={e.status === 'closed'} />
          <EngagementPortal engagementId={id} />
          {canSupport && <EngagementSupportCard engagement={e} locked={locked} />}
          {canProjects && (
            <Card className="p-4">
              <ChangeRequests engagementId={id} archived={!!e.archivedAt} />
            </Card>
          )}
          <RecordTasks on={{ type: 'engagement', id }} archived={!!e.archivedAt} />
          {canCrm && (
            <Card className="grid content-start gap-3 p-4">
              <h2 className="text-lg font-bold">{t('activities.title')}</h2>
              <Activities organisationId={e.organisationId} engagementId={id} />
            </Card>
          )}
          <AttachmentsPanel type="engagement" id={id} archived={!!e.archivedAt} />
        </div>
      </div>
    </>
  );
}

function Rates({ engagementId, currency, locked }: { engagementId: string; currency: string; locked: boolean }) {
  const { t } = useTranslation();
  const canCreate = useCan('engagements:create');
  const canDelete = useCan('engagements:delete');
  const rates = useQuery(ratesQuery({ engagementId }));
  const [open, setOpen] = useState(false);
  const remove = useActionMutation(deleteRate, { invalidate: [['engagement', 'rate']] });
  return (
    <Card className="grid content-start gap-3 p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-bold">{t('engagements.rates.title')}</h2>
        {canCreate && !locked && (
          <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
            <Plus /> {t('engagements.rates.new')}
          </Button>
        )}
      </div>
      <p className="text-xs text-fg-3">{t('engagements.rates.hint')}</p>
      {rates.isLoading ? (
        <Skeleton className="h-24" />
      ) : !rates.data?.items.length ? (
        <EmptyState title={t('engagements.rates.empty')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('engagements.rates.validFrom')}</Th>
              <Th>{t('engagements.rates.level')}</Th>
              <Th className="text-right">{t('engagements.rates.sell')}</Th>
              <Th className="text-right">{t('engagements.rates.cost')}</Th>
              <Th className="sr-only">{t('common.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rates.data.items.map((r) => (
              <Tr key={r.id}>
                <Td className="tabular">{r.validFrom}</Td>
                <Td className="text-fg-2">{r.workTypeId ? t('engagements.rates.workTypeLevel') : t('engagements.rates.engagementLevel')}</Td>
                <Td className="text-right tabular">{r.sellCents !== null ? formatMoney(r.sellCents, r.currency) : '–'}</Td>
                <Td className="text-right tabular text-fg-2">{r.costCents !== null ? formatMoney(r.costCents, r.currency) : '–'}</Td>
                <Td className="text-right">
                  {canDelete && !locked && (
                    <Button size="icon" variant="ghost" aria-label={t('engagements.rates.delete')} onClick={() => remove.mutate({ input: { id: r.id }, ifMatch: r.version })}>
                      <Trash2 />
                    </Button>
                  )}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      <NewRate open={open} engagementId={engagementId} currency={currency} onClose={() => setOpen(false)} />
    </Card>
  );
}

function NewRate({ open, engagementId, currency, onClose }: { open: boolean; engagementId: string; currency: string; onClose: () => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const [key] = useState(newIdempotencyKey);
  const [validFrom, setValidFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [sell, setSell] = useState('');
  const [cost, setCost] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useActionMutation(createRate, { invalidate: [['engagement', 'rate']], silentErrors: true, onSuccess: onClose });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('engagements.rates.new')}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label={t('engagements.rates.validFrom')} error={errors.validFrom}>
            {(p) => <Input {...p} type="date" value={validFrom} onChange={(ev) => setValidFrom(ev.target.value)} />}
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={`${t('engagements.rates.sell')} (${currency}/h)`} error={errors.sellCents}>
              {(p) => <Input {...p} autoFocus inputMode="decimal" value={sell} onChange={(ev) => setSell(ev.target.value)} placeholder="125.00" />}
            </Field>
            <Field label={`${t('engagements.rates.cost')} (${currency}/h)`} hint={t('common.optional')}>
              {(p) => <Input {...p} inputMode="decimal" value={cost} onChange={(ev) => setCost(ev.target.value)} />}
            </Field>
          </div>
          {errors._ && (
            <p role="alert" className="text-sm text-danger">
              {errors._}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            loading={create.isPending}
            onClick={() =>
              create.mutate(
                { input: { level: 'engagement', engagementId, validFrom, sellCents: inputToCents(sell), costCents: inputToCents(cost), currency }, key },
                { onError: (err) => setErrors(err instanceof ApiError ? { ...err.fieldErrors(), _: message(err) } : { _: message(err) }) },
              )
            }
          >
            {t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
